import {
  andAllWhere,
  buildQuery,
  noop,
  Ops,
  orderBy,
  OrderVariant,
  page,
  where,
} from '@albatrosdeveloper/ave-utils-npm/lib/utils/query.util';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import OrderAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/process/order/order.entity';
import { IPagination, IPaginationResultFrequencies } from 'app/common/interfaces';
import { Observable } from 'rxjs';
import { environment as env } from '../../../../../../environments/environment';

const apiUrl = env.API_AVE_ORDER_URL;
const moduleName = 'order';

/**
 * Alcance completo de una consulta de órdenes a planificar.
 *
 * Existe como objeto y no como lista de parámetros porque las pantallas hacen
 * **preguntas distintas sobre el mismo maestro**: "qué falta repartir" (sin
 * viaje, sin fecha), "qué está asignado hoy" (con viaje, día de carga) y "qué
 * quedó sin atender" (viaje ya cerrado, últimos meses). Cada pestaña de
 * Asignación de Carga es una de esas preguntas, y la sugerencia de optimización
 * tiene que reproducir **exactamente** la de la pestaña activa: si optimizara un
 * universo distinto al de la grilla, el conteo no cuadraría con lo que el
 * usuario tiene delante.
 */
/** Los dos campos de fecha por los que se puede acotar una orden. */
export type DateField = 'loadDateTime' | 'deliveryDateTime';

/**
 * NO agregar un filtro "este campo de fecha está vacío" (`Ops.eq(null)` sobre
 * `loadDateTime` / `deliveryDateTime`).
 *
 * Se intentó, para traer los pedidos que nadie programó todavía. El backend no lo
 * rechaza: **no responde**. `withCount` calcula además el total, así que la
 * consulta cuenta todo el histórico sin fecha de carga y se pasa de los 20 s de
 * tope. Medido contra Ofic. Germania.
 *
 * El superconjunto SÍ es rápido —`trip == null` + `statusId == 1` + oficina, sin
 * ningún filtro de fecha, que es lo que corre la pestaña "Pedidos sin viaje"—, así
 * que omitir la fecha deja usar los índices y preguntar por un nulo sobre una
 * fecha fuerza el scan. Si algún día hace falta, la salida es del lado del
 * servicio de órdenes (un índice, o un endpoint sin `withCount`), no del cliente.
 *
 * `Ops.eq(null)` sobre `trip` sí funciona: es una referencia, no una fecha.
 */

export interface DeliveryOrdersQuery {
  /**
   * Compañía (id numérico). Ausente = sin filtro de compañía.
   *
   * Se puede omitir porque la oficina de ventas ya acota el universo y cada
   * orden trae su propia compañía: exigirla obligaría a elegir una en el filtro
   * para algo que la consulta no necesita.
   */
  companyId?: number;
  /** Oficina de ventas (id numérico). Obligatoria: es el almacén del plan. */
  salesOfficeId: number;
  /** Clase de documento (`DT003` = entrega). */
  typeCode?: string;
  /** Campo y rango de fecha. Ausente = sin filtro de fecha. */
  date?: { field: DateField; start: string; end: string };
  /**
   * Solo órdenes que **tengan** ese campo.
   *
   * La pestaña "Pedidos con ruta y viaje" exige `deliveryDateTime != null`
   * (`loads.service.ts`, `getTripsByLoadDocs`) y la optimización no lo hacía, así
   * que podía planificar pedidos que la grilla no muestra y el conteo no cerraba.
   */
  requireDate?: DateField;
  /** Acota a estos territorios (ids numéricos). */
  routeIds?: number[];
  /** Acota a estas sub-rutas (ids numéricos). */
  subRouteIds?: number[];
  /** Acota a estos clientes (`shipTo.id` numéricos). */
  customerIds?: number[];
  /** Acota a estas clases de documento (códigos de `class.code`). */
  documentClassCodes?: string[];
  /** `true` = solo con viaje; `false` = solo sin viaje; ausente = ambas. */
  hasTrip?: boolean;
  /**
   * Solo órdenes cuyo viaje YA terminó (`trip.dateHourEnd != null`): es lo que
   * define "sin atención pasada" — salió en un viaje que ya cerró y aun así no
   * se entregó.
   */
  tripClosed?: boolean;
  /** Excluye los documentos de carga unificados (`operationType == 4`). */
  excludeUnified?: boolean;
  /**
   * `true` = solo `statusId == 1` (pendientes, lo que muestran las pestañas de
   * Asignación de Carga). `false` = `statusId > 0`, es decir todo lo no anulado,
   * incluidas entregadas y parciales — el alcance de Rutas AI, que re-planifica
   * el día completo.
   */
  onlyPending?: boolean;
  options?: IPagination<OrderAttributes>;
}

/**
 * Órdenes de entrega (`DT003`) que alimentan la optimización de rutas.
 *
 * Estas consultas vivían en `LoadsService` y luego dentro de la pantalla "Rutas
 * AI". Viven acá, junto al resto del kit de optimización (contrato, mapper,
 * partición, flota), porque **dos** pantallas planifican con ellas: Rutas AI y
 * la sugerencia del tab "Pedidos con ruta y viaje" de Asignación de Carga. Una
 * copia por pantalla haría que un filtro corregido en una siguiera mal en la
 * otra.
 *
 * NO extiende `AbstractService`: la API de órdenes se consume acá con la
 * convención de query del repo (`buildQuery` + `withCount`), no con el CRUD
 * genérico.
 *
 * OJO con la proyección: ambas pegan a `api/order/withCount`, que **no acepta
 * `select`** — el servicio responde `400`, con rutas anidadas o con claves de
 * primer nivel. Antes de intentar recortar la respuesta hay que verificar con el
 * equipo del servicio de órdenes qué admite ese endpoint.
 */
@Injectable({ providedIn: 'root' })
export class DeliveryOrdersService {
  /**
   * Header que le pide al `fuseLoadingInterceptor` NO mostrar el overlay global.
   *
   * Una corrida pagina hasta 50 páginas por oficina y por conjunto, y las dos
   * pantallas que optimizan muestran su propio progreso: sin esto el overlay de
   * AVE parpadea sobre el modal una vez por página. Mismo valor y mismo motivo
   * que en `RouteOptimizationService` y en las tareas de export.
   */
  private readonly silentLoading = { 'x-ave-ctx': 'skip-loading' };

  constructor(private readonly http: HttpClient) {}

  /**
   * Órdenes a planificar de UNA oficina de ventas, con el alcance que pida quien
   * consulta.
   *
   * Es el único constructor de query del servicio: los dos métodos de abajo son
   * atajos que delegan acá. Así el filtro de anuladas, la paginación y el orden
   * se corrigen en un solo lugar.
   */
  findForPlanning(
    query: DeliveryOrdersQuery,
  ): Observable<IPaginationResultFrequencies<OrderAttributes>> {
    if (query?.salesOfficeId == null) {
      throw new Error('salesOfficeId es requerido.');
    }
    // La fecha es opcional, pero un rango a medias es un error de quien llama, no
    // "sin filtro".
    if (query.date && (!query.date.start || !query.date.end)) {
      throw new Error('rango de fecha es requerido.');
    }

    const built = buildQuery<any>(
      where('type.code' as keyof OrderAttributes, Ops.eq(query.typeCode ?? 'DT003')),
      query.companyId ? andAllWhere('company.id', Ops.eq(query.companyId)) : noop(),
      andAllWhere('salesOffice.id', Ops.eq(query.salesOfficeId)),
      query.routeIds?.length ? andAllWhere('routes.id', Ops.in(...query.routeIds)) : noop(),
      query.subRouteIds?.length
        ? andAllWhere('subRoute.id', Ops.in(...query.subRouteIds))
        : noop(),
      query.customerIds?.length ? andAllWhere('shipTo.id', Ops.in(...query.customerIds)) : noop(),
      query.documentClassCodes?.length
        ? andAllWhere('class.code', Ops.in(...query.documentClassCodes))
        : noop(),
      query.hasTrip === undefined
        ? noop()
        : andAllWhere('trip', query.hasTrip ? Ops.not(null) : Ops.eq(null)),
      query.tripClosed ? andAllWhere('trip.dateHourEnd', Ops.not(null)) : noop(),
      // 4 = documento de carga unificado: no es una entrega a planificar.
      query.excludeUnified ? andAllWhere('operationType', Ops.not(4)) : noop(),
      query.date ? andAllWhere(query.date.field, Ops.btwnDates(query.date.start, query.date.end)) : noop(),
      query.requireDate ? andAllWhere(query.requireDate, Ops.not(null)) : noop(),
      // statusId 0 = documento anulado; los válidos son siempre > 0 (1, 9, 10,
      // 11, 18…), y `gt` es el operador mejor soportado por el parser del
      // backend. `_deleted` no se filtra aquí: no es atributo del modelo (el
      // backend lo excluye por defecto) y el parser vacía el query.
      query.onlyPending ? andAllWhere('statusId', Ops.eq(1)) : andAllWhere('statusId', Ops.gt(0)),
      orderBy('creationDateTime', OrderVariant.Desc),
      page(query.options?.currentPage ?? 1, query.options?.limitPerPage ?? 500),
    );

    const API_URL = `${apiUrl}api/${moduleName}/withCount?filter=${encodeURIComponent(
      JSON.stringify(built),
    )}`;
    return this.http.get<IPaginationResultFrequencies<OrderAttributes>>(API_URL, {
      headers: this.silentLoading,
    });
  }

  /**
   * Órdenes de entrega (DT003) de una oficina de ventas **sin viaje asignado**,
   * opcionalmente acotadas por territorios. Sin filtro de fecha.
   */
  findDeliveryOrdersWithoutTrip(
    companyId: number,
    salesOfficeId: number,
    routeIds?: number[],
    typeCode: string = 'DT003',
    options?: IPagination<OrderAttributes>,
  ): Observable<IPaginationResultFrequencies<OrderAttributes>> {
    if (!companyId) {
      throw new Error('companyId es requerido.');
    }
    return this.findForPlanning({
      companyId,
      salesOfficeId,
      typeCode,
      routeIds,
      hasTrip: false,
      options,
    });
  }

  /**
   * Órdenes de entrega (DT003) de una oficina de ventas por fecha — de **carga**
   * (`loadDateTime`) o de **entrega** (`deliveryDateTime`) según `dateField` —,
   * tengan o no viaje, opcionalmente acotadas por territorios. Es el alcance de
   * "Rutas AI", que re-planifica todo el volumen del día.
   */
  findDeliveryOrdersByDate(
    companyId: number,
    salesOfficeId: number,
    dateField: DateField,
    date: { start: string; end: string },
    routeIds?: number[],
    typeCode: string = 'DT003',
    options?: IPagination<OrderAttributes>,
  ): Observable<IPaginationResultFrequencies<OrderAttributes>> {
    if (!companyId) {
      throw new Error('companyId es requerido.');
    }
    if (!date?.start || !date?.end) {
      throw new Error('rango de fecha es requerido.');
    }
    return this.findForPlanning({
      companyId,
      salesOfficeId,
      typeCode,
      date: { field: dateField, start: date.start, end: date.end },
      routeIds,
      options,
    });
  }
}
